import { Link } from 'react-router-dom';
import { useOverview } from '@/api/hooks';
import { useAgent } from '@/auth/use-agent';
import type { EventItem, OverviewTournament } from '@/api/types';
import { formatCount, formatDate, formatDateTime, formatPercent } from '@/lib/format';
import { toPlayerId } from '@/lib/player-id';
import { Badge } from '@/ui/Badge';
import { Card } from '@/ui/Card';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { Loading } from '@/ui/Loading';
import { Notice } from '@/ui/Notice';
import { Stat } from '@/ui/Stat';
import { Table, Td, Th } from '@/ui/Table';

function tournamentLabel(tournament: OverviewTournament): string {
  return tournament.title || tournament.tournamentId;
}

function TournamentsTable({ tournaments }: { tournaments: OverviewTournament[] }) {
  return (
    <Table>
      <thead>
        <tr>
          <Th align="left">Tournament</Th>
          <Th align="left">Status</Th>
          <Th align="left">Starts</Th>
          <Th align="left">Ends</Th>
          <Th>Links</Th>
          <Th>Invited</Th>
          <Th>Joined</Th>
          <Th>Join rate</Th>
          <Th align="left">Last activity</Th>
        </tr>
      </thead>
      <tbody>
        {tournaments.length === 0 && (
          <tr>
            <Td align="left" colSpan={9} className="text-slate-500">
              No links have been issued yet.
            </Td>
          </tr>
        )}
        {tournaments.map((tournament) => (
          <tr key={tournament.tournamentId}>
            <Td align="left">
              <Link
                to={`/tournaments/${encodeURIComponent(tournament.tournamentId)}`}
                className="font-medium text-blue-700 hover:underline"
              >
                {tournamentLabel(tournament)}
              </Link>
              {tournament.title && (
                <span className="ml-2 font-mono text-xs text-slate-400">{tournament.tournamentId}</span>
              )}
            </Td>
            <Td align="left">
              <Badge value={tournament.status} />
            </Td>
            <Td align="left">{formatDate(tournament.startAt)}</Td>
            <Td align="left">{formatDate(tournament.endAt)}</Td>
            <Td>{formatCount(tournament.linksIssued)}</Td>
            <Td>{formatCount(tournament.playersInvited)}</Td>
            <Td>{formatCount(tournament.playersRedeemed)}</Td>
            <Td>{formatPercent(tournament.redeemRate)}</Td>
            <Td align="left">{formatDateTime(tournament.lastEventAt)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function RecentActivity({ events, titles }: { events: EventItem[]; titles: Map<string, string> }) {
  if (events.length === 0) return <p className="text-sm text-slate-500">Nothing recorded yet.</p>;
  return (
    <ul className="divide-y divide-slate-100 text-sm">
      {events.map((event) => (
        <li
          key={`${event.linkId}-${event.event}-${event.occurredAt}`}
          className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
        >
          <span className="w-36 shrink-0 text-slate-500">{formatDateTime(event.occurredAt)}</span>
          <Badge value={event.event} />
          <span className="font-mono text-xs">{toPlayerId(event.playerUuid)}</span>
          <span className="text-slate-500">by {event.agentId}</span>
          <Link to={`/tournaments/${encodeURIComponent(event.tournamentId)}`} className="text-blue-700 hover:underline">
            {titles.get(event.tournamentId) || event.tournamentId}
          </Link>
          {event.alreadyJoined && <span className="text-xs text-slate-500">(was already in)</span>}
        </li>
      ))}
    </ul>
  );
}

export function DashboardPage() {
  const { agent } = useAgent();
  const overview = useOverview();

  if (!agent.trackingEnabled) {
    return <Notice>Tracking is not enabled on this environment, so there are no results here.</Notice>;
  }
  if (overview.isPending) return <Loading what="dashboard" />;
  if (overview.error) return <ErrorNotice error={overview.error} />;

  const data = overview.data;
  const titles = new Map(data.tournaments.map((tournament) => [tournament.tournamentId, tournamentLabel(tournament)]));

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Dashboard</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Links issued" value={formatCount(data.totals.linksIssued)} />
        <Stat label="Players invited" value={formatCount(data.totals.playersInvited)} />
        <Stat label="Players joined" value={formatCount(data.totals.playersRedeemed)} />
        <Stat
          label="Join rate"
          value={formatPercent(data.totals.redeemRate)}
          hint="Joined players over invited players"
        />
      </div>

      <Card
        title="Tournaments"
        description={
          agent.admin ? 'Every tournament with links, all agents.' : 'Every tournament you issued links for.'
        }
      >
        <TournamentsTable tournaments={data.tournaments} />
      </Card>

      {agent.admin && (
        <Card title="Agents">
          <Table>
            <thead>
              <tr>
                <Th align="left">Agent</Th>
                <Th>Links</Th>
                <Th>Invited</Th>
                <Th>Joined</Th>
                <Th>Join rate</Th>
                <Th align="left">Last activity</Th>
              </tr>
            </thead>
            <tbody>
              {data.agents.map((row) => (
                <tr key={row.agentId}>
                  <Td align="left">{row.agentId}</Td>
                  <Td>{formatCount(row.linksIssued)}</Td>
                  <Td>{formatCount(row.playersInvited)}</Td>
                  <Td>{formatCount(row.playersRedeemed)}</Td>
                  <Td>{formatPercent(row.redeemRate)}</Td>
                  <Td align="left">{formatDateTime(row.lastEventAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <Card title="Recent activity" description={`Newest first. ${data.eventsCount} events recorded in total.`}>
        <RecentActivity events={data.recent} titles={titles} />
      </Card>
    </div>
  );
}
